import { forwardRef, memo, useMemo } from 'react';

import Icons from '../../Elements/Icons/index.ts';
import { SubSection, Title } from './components/index.ts';
import type { SubSectionProps } from './components/SubSection/index.tsx';
import { useExpandCollapse } from './hooks/index.ts';
import { ItemsWrapper, SectionWrapper } from './styled.ts';
import type { SectionProps } from './types.ts';

const SectionComponent = memo(
  forwardRef<HTMLDivElement, SectionProps>(function SectionComponent(props, ref) {
    const {
      children,
      id,
      className,
      dataTestId,
      title,
      tooltip,
      tag,
      actions,
      icon,
      iconPosition,
      onIconClick,
      height,
      expanded,
      inner,
      itemsStyles,
      showExpandIcon,
    } = props;

    const { getCollapseProps, isExpanded } = useExpandCollapse(expanded);
    const hasTitle = !!(title || actions);
    const hasFixedHeight = height !== undefined;

    const expandIcon = useMemo(
      () => (showExpandIcon ? <Icons.Chevron align={isExpanded ? 'bottom' : 'top'} /> : null),
      [showExpandIcon, isExpanded],
    );
    const finalIcon = icon ?? expandIcon;

    return (
      <SectionWrapper id={id} className={className} height={height} $inner={inner} ref={ref} data-testid={dataTestId}>
        {hasTitle && (
          <Title
            title={title}
            tooltip={tooltip}
            tag={tag}
            actions={actions}
            icon={finalIcon}
            iconPosition={iconPosition}
            onIconClick={onIconClick}
          />
        )}
        <ItemsWrapper
          {...getCollapseProps()}
          $hasTitle={hasTitle}
          $isExpanded={isExpanded}
          $hasFixedHeight={hasFixedHeight}
          $customStyles={itemsStyles}>
          {children}
        </ItemsWrapper>
      </SectionWrapper>
    );
  }),
);

export const Section = Object.assign(SectionComponent, {
  Item: SubSection,
});

export type { SectionProps, SubSectionProps };
